import { useEffect, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FullPageSpinner, PermissionProvider, useAuth, type PermissionSnapshot } from '@concours/ui'
import { onPermVersion } from './session'

export interface MyPermissions {
  version: number
  global: string[]
  scoped: PermissionSnapshot['scoped']
  roles: {
    assignmentId: number
    code: string
    label: string
    scopeType: string
    scopeId: string | null
    requiresMfa: boolean
  }[]
  requiresMfa: boolean
  isSuperAdmin: boolean
  mfaEnabled: boolean
  mfaSetupRequired: boolean
}

export const MY_PERMISSIONS_KEY = ['me', 'permissions'] as const

/** Permissions effectives du compte connecté (source : API, jamais le portail). */
export function useMyPermissions() {
  const { session, status, user } = useAuth()
  return useQuery({
    queryKey: [...MY_PERMISSIONS_KEY, user?.id],
    queryFn: () => session.api.get<MyPermissions>('/auth/admin/me/permissions'),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  })
}

/**
 * Charge les permissions effectives et les fournit à l'interface (`useCan`, `<Can>`,
 * menu). Rechargées automatiquement quand l'API annonce une nouvelle version
 * (profil modifié, affectation ajoutée ou retirée…).
 * ⚠️ Confort d'affichage : l'API contrôle chaque requête.
 */
export function LivePermissionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const permissions = useMyPermissions()
  const current = permissions.data?.version

  useEffect(
    () =>
      onPermVersion((version) => {
        if (current !== undefined && version !== current) {
          void queryClient.invalidateQueries({ queryKey: MY_PERMISSIONS_KEY })
        }
      }),
    [current, queryClient]
  )

  if (permissions.isPending) return <FullPageSpinner />
  const snapshot: PermissionSnapshot = permissions.data
    ? {
        global: permissions.data.global,
        scoped: permissions.data.scoped,
        version: permissions.data.version,
      }
    : { global: [] }
  return <PermissionProvider snapshot={snapshot}>{children}</PermissionProvider>
}
